import type { SessionDetail } from "@/lib/api";

export type SpanKind =
  | "spec"
  | "spec-accepted"
  | "spec-discarded"
  | "regen"
  | "accepted"
  | "discarded"
  | "degrade"
  | "recovery"
  | "guard"
  | "in"
  | "outcome"
  | "income"
  | "tool"
  | "user"
  | "assistant";

export type Span = {
  id: string;
  lane: "stt" | "llm" | "tts" | "quality" | "aux" | "perception" | "transcript" | "delegation";
  startMs: number;
  endMs: number;
  kind?: SpanKind;
  label?: string;
  text?: string;
  prompt?: string;
  provider?: string;
  model?: string;
  audioUrl?: string;
  frameUrl?: string;
  linkedToId?: string;
  linkedFromId?: string;
  drivenBySttId?: string;
  drivesRegenId?: string;
  channel?: string;
  delegationId?: string;
  display?: "bar" | "chip";
};

export type PipelineEvent = {
  event?: string;
  t_ns?: number;
  pipeline?: string;
  meta?: Record<string, unknown>;
};

export const RECOVERY_COLOR = "#0284c7";
export const DEGRADE_COLOR = "#f59e0b";

export const LANE_COLOR: Record<Span["lane"], string> = {
  stt: "#0968f2",
  llm: "#8057e8",
  tts: "#0aa878",
  quality: DEGRADE_COLOR,
  aux: "#ef4d55",
  perception: "#0d9488",
  transcript: "#0968f2",
  delegation: "#8057e8",
};

export const LANE_FILL: Record<Span["lane"], string> = {
  stt: "#edf5ff",
  llm: "#f3effc",
  tts: "#e8f8f2",
  quality: "#fff7e8",
  aux: "#fdecee",
  perception: "#ecfdf8",
  transcript: "#edf5ff",
  delegation: "#f3effc",
};

export const CASCADE_LEGEND: Array<{ label: string; color: string; className?: string }> = [
  { label: "STT", color: LANE_COLOR.stt },
  { label: "LLM", color: LANE_COLOR.llm },
  { label: "Spec accepted", color: LANE_COLOR.llm, className: "hatched accepted" },
  { label: "Spec discarded", color: LANE_COLOR.llm, className: "hatched discarded" },
  { label: "LLM regen", color: LANE_COLOR.llm, className: "regen" },
  { label: "TTS", color: LANE_COLOR.tts },
  { label: "Guard", color: LANE_COLOR.aux },
  { label: "Live Perception", color: LANE_COLOR.perception },
  { label: "Degrade", color: DEGRADE_COLOR },
  { label: "Recovery", color: RECOVERY_COLOR },
];

export const DIRECT_LEGEND: Array<{ label: string; color: string; className?: string }> = [
  { label: "User", color: "#0968f2" },
  { label: "Mouth", color: "#0aa878" },
  { label: "Delegation", color: LANE_COLOR.delegation },
  { label: "In", color: "#5b8def" },
  { label: "Outcome", color: "#8057e8" },
  { label: "Income", color: "#c084fc" },
  { label: "Guard", color: LANE_COLOR.aux },
  { label: "Live Perception", color: LANE_COLOR.perception },
  { label: "Degrade / Failover", color: DEGRADE_COLOR },
];

/** LiveAvatar FULL — LA hosts ASR/LLM/TTS; Pulse has transcript chips + optional LP only. */
export const LIVEAVATAR_LEGEND: Array<{ label: string; color: string; className?: string }> = [
  { label: "User", color: "#0968f2" },
  { label: "Avatar", color: "#ea580c" },
  { label: "Live Perception", color: LANE_COLOR.perception },
  { label: "Session", color: LANE_COLOR.aux },
];

export const CASCADE_LANES: Span["lane"][] = ["stt", "llm", "tts", "aux", "perception", "quality"];
export const DIRECT_LANES: Span["lane"][] = ["transcript", "delegation", "aux", "perception", "quality"];
export const LIVEAVATAR_LANES: Span["lane"][] = ["transcript", "aux", "perception"];

export type TimelineMode = "cascade" | "direct" | "liveavatar";

export function isDirectTimeline(detail: SessionDetail, events: PipelineEvent[]): boolean {
  return resolveTimelineMode(detail, events) === "direct";
}

export function resolveTimelineMode(detail: SessionDetail, events: PipelineEvent[] = []): TimelineMode {
  const mode = String(detail.agentMode || "").toLowerCase();
  if (mode === "liveavatar" || mode === "live_avatar" || mode === "live-avatar") {
    return "liveavatar";
  }
  if (mode === "direct") return "direct";
  for (const e of events) {
    const name = String(e.event || "");
    const pipe = String(e.pipeline || "");
    if (name.startsWith("liveavatar.") || pipe.includes("liveavatar")) return "liveavatar";
    if (name.startsWith("direct.") || pipe.includes("direct")) return "direct";
  }
  return "cascade";
}

function msFromNs(tNs: number, t0: number) {
  return Math.max(0, (tNs - t0) / 1e6);
}

function clipChip(text: string, n = 22) {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n - 1)}…`;
}

function pushQualityFromEvent(
  spans: Span[],
  name: string,
  t: number,
  meta: Record<string, unknown>,
  id: string,
): boolean {
  if (name === "provider.failover") {
    const fromP = String(meta.from_provider || "primary");
    const toP = String(meta.to_provider || "failover");
    const fromM = String(meta.from_model || "");
    const toM = String(meta.to_model || "");
    spans.push({
      id,
      lane: "quality",
      startMs: t,
      endMs: t,
      kind: "degrade",
      label: "Failover",
      text: `${fromP}${fromM ? `/${fromM}` : ""} → ${toP}${toM ? `/${toM}` : ""}`,
      provider: toP,
      model: toM || undefined,
    });
    return true;
  }
  if (name === "provider.aux_failover") {
    const service = String(meta.service || "aux");
    const fromM = String(meta.from_model || "primary");
    const toM = String(meta.to_model || "fallback");
    const reason = String(meta.reason || "").trim();
    spans.push({
      id,
      lane: "quality",
      startMs: t,
      endMs: t,
      kind: "degrade",
      label: "AUX failover",
      text: `${service}: ${fromM} → ${toM}${reason ? ` (${reason})` : ""}`,
      model: toM || undefined,
    });
    return true;
  }
  return false;
}

function appendVideoQuality(spans: Span[], detail: SessionDetail) {
  if (detail.providerDegraded && !spans.some((s) => s.lane === "quality")) {
    const at =
      spans.find((s) => s.lane === "llm" && (s.kind === "accepted" || s.kind === "regen"))?.startMs
      ?? spans.find((s) => s.lane === "delegation")?.startMs
      ?? Math.max(0, Math.max(...spans.map((s) => s.endMs), 1000) * 0.35);
    spans.push({
      id: "q-degrade",
      lane: "quality",
      startMs: at,
      endMs: at,
      kind: "degrade",
      label: "Degrade",
      text: detail.degradedReason || detail.lastProviderError || "Provider failover",
    });
  }
}

function pushAuxFromEvent(spans: Span[], name: string, t: number, meta: Record<string, unknown>, i: { n: number }) {
  if (name === "aux.guardrail" && Boolean(meta.yes)) {
    const lat = Math.max(80, Number(meta.latency_ms) || 120);
    const start = Math.max(0, t - lat);
    spans.push({
      id: `aux-guard-${i.n++}`,
      lane: "aux",
      startMs: start,
      endMs: Math.max(start + 80, t),
      kind: "guard",
      label: "Guard",
      text: String(meta.text || ""),
      model: meta.model != null ? String(meta.model) : undefined,
      display: "bar",
    });
  } else if (name === "tool.guardrail_redirect") {
    spans.push({
      id: `aux-redir-${i.n++}`,
      lane: "aux",
      startMs: Math.max(0, t - 40),
      endMs: t + 80,
      kind: "guard",
      label: meta.style === "adaptive" ? "Redirect (adaptive)" : "Redirect (fixed)",
      text: String(meta.text || ""),
      display: "bar",
    });
  }
}

export function buildDirectSpansFromEvents(events: PipelineEvent[], detail: SessionDetail): Span[] | null {
  if (!events.length) return null;
  const withT = events.filter((e) => typeof e.t_ns === "number");
  if (!withT.length) return null;
  const t0 = Math.min(...withT.map((e) => Number(e.t_ns)));
  const spans: Span[] = [];
  const open = new Map<string, Span>();
  const i = { n: 0 };
  let trailOffset = 0;

  for (const e of withT) {
    const name = String(e.event || "");
    const t = msFromNs(Number(e.t_ns), t0);
    const meta = e.meta || {};
    pushAuxFromEvent(spans, name, t, meta, i);
    pushQualityFromEvent(spans, name, t, meta, `q-${i.n++}`);

    if (name === "direct.transcript") {
      const role = String(meta.role || "user");
      const text = String(meta.text || "");
      const mouth = role === "assistant" || role === "mouth";
      spans.push({
        id: `tx-${i.n++}`,
        lane: "transcript",
        startMs: t,
        endMs: t + 700,
        kind: mouth ? "assistant" : "user",
        label: mouth ? clipChip(text) || "Mouth" : clipChip(text) || "User",
        text,
        display: "chip",
      });
    } else if (name === "direct.delegation_start") {
      const did = String(meta.delegation_id || `d-${i.n}`);
      trailOffset = 0;
      const span: Span = {
        id: `del-${did}`,
        lane: "delegation",
        startMs: t,
        endMs: t + 400,
        label: "Delegation",
        text: did,
        delegationId: did,
        display: "bar",
      };
      spans.push(span);
      open.set(did, span);
    } else if (name === "direct.delegation_in" || name === "direct.delegation_outcome" || name === "direct.delegation_income" || name === "direct.tool") {
      const did = String(meta.delegation_id || "");
      const parent = did ? open.get(did) : undefined;
      const kind: SpanKind =
        name === "direct.delegation_in"
          ? "in"
          : name === "direct.delegation_outcome"
            ? "outcome"
            : name === "direct.tool"
              ? "tool"
              : "income";
      const label =
        kind === "in" ? "In" : kind === "outcome" ? "Outcome" : kind === "tool" ? String(meta.tool || meta.name || "Tool") : "Income";
      const chipStart = t + trailOffset;
      trailOffset += 90;
      spans.push({
        id: `trail-${kind}-${i.n++}`,
        lane: "delegation",
        startMs: chipStart,
        endMs: chipStart + 520,
        kind,
        label,
        text: String(meta.text || meta.url || meta.tool || meta.name || ""),
        channel: meta.channel != null ? String(meta.channel) : undefined,
        delegationId: did || undefined,
        display: "chip",
      });
      if (parent) parent.endMs = Math.max(parent.endMs, chipStart + 520);
    } else if (name === "direct.delegation_end") {
      const did = String(meta.delegation_id || "");
      const parent = did ? open.get(did) : undefined;
      if (parent) {
        parent.endMs = Math.max(parent.endMs, t);
        parent.label = meta.status && meta.status !== "ok" ? `Delegation (${meta.status})` : "Delegation";
        open.delete(did);
      }
    }
  }

  appendVideoQuality(spans, detail);
  return spans.length ? spans : null;
}

export function buildDirectSpansFromTurns(detail: SessionDetail): Span[] {
  const spans: Span[] = [];
  const lines = (detail.transcripts || []).length
    ? detail.transcripts || []
    : (detail.turns || []).map((t) => ({
        role: t.role,
        text: t.text,
        startMs: t.startMs,
      }));
  lines.forEach((row, i) => {
    const start = Number(row.startMs ?? i * 2000);
    const role = String(row.role || "");
    const text = String(row.text || "");
    const mouth = role === "assistant" || role === "agent";
    spans.push({
      id: `tx-turn-${i}`,
      lane: "transcript",
      startMs: start,
      endMs: start + 700,
      kind: mouth ? "assistant" : "user",
      label: clipChip(text) || (mouth ? "Mouth" : "User"),
      text,
      display: "chip",
    });
  });
  appendVideoQuality(spans, detail);
  return spans;
}

/** LiveAvatar FULL: transcript chips only (no Cascade STT/LLM/TTS invent from turns). */
export function buildLiveAvatarSpansFromTurns(detail: SessionDetail): Span[] {
  const spans: Span[] = [];
  const lines = (detail.transcripts || []).length
    ? detail.transcripts || []
    : (detail.turns || []).map((t) => ({
        role: t.role,
        text: t.text,
        startMs: t.startMs,
      }));
  lines.forEach((row, i) => {
    const start = Number(row.startMs ?? i * 2000);
    const role = String(row.role || "");
    const text = String(row.text || "");
    const avatar = role === "assistant" || role === "agent" || role === "bot";
    spans.push({
      id: `la-tx-${i}`,
      lane: "transcript",
      startMs: start,
      endMs: start + 700,
      kind: avatar ? "assistant" : "user",
      label: clipChip(text) || (avatar ? "Avatar" : "User"),
      text,
      display: "chip",
    });
  });
  return spans;
}

export function buildLiveAvatarSpansFromEvents(
  events: PipelineEvent[],
  detail: SessionDetail,
): Span[] | null {
  if (!events.length) return null;
  const withT = events.filter((e) => typeof e.t_ns === "number");
  if (!withT.length) return null;
  const t0 = Math.min(...withT.map((e) => Number(e.t_ns)));
  const spans: Span[] = [];
  let i = 0;
  for (const e of withT) {
    const name = String(e.event || "");
    const meta = (e.meta && typeof e.meta === "object" ? e.meta : {}) as Record<string, unknown>;
    const t = msFromNs(Number(e.t_ns), t0);
    if (name === "transcript.user" || name === "transcript.assistant") {
      const text = String(meta.text || "");
      const avatar = name === "transcript.assistant";
      spans.push({
        id: `la-ev-tx-${i++}`,
        lane: "transcript",
        startMs: t,
        endMs: t + 700,
        kind: avatar ? "assistant" : "user",
        label: clipChip(text) || (avatar ? "Avatar" : "User"),
        text,
        display: "chip",
      });
    } else if (name.startsWith("liveavatar.full.")) {
      // Pulse SessionEventLog stamps (semantic_endpointing / hangup_tool / idle_presence)
      // are Cascade knobs forced off for FULL — do not dump that JSON on the chip.
      const short = name.replace(/^liveavatar\.full\./, "");
      const joined = meta.joined_room === true;
      spans.push({
        id: `la-ev-${i++}`,
        lane: "aux",
        startMs: t,
        endMs: t + 400,
        label: short === "ready" ? "LA ready" : `LA ${short}`,
        text:
          short === "ready"
            ? joined
              ? "Pulse observer joined LaRoom"
              : "Pulse keep-alive (no Cascade EOT / hangup tool / idle)"
            : `LiveAvatar FULL ${short}`,
        display: "bar",
      });
    }
  }
  // Lifecycle events alone must not hide Dynamo / meta transcript chips.
  if (!spans.some((s) => s.lane === "transcript")) {
    spans.push(...buildLiveAvatarSpansFromTurns(detail));
  }
  return spans.length ? spans : null;
}

export function buildSpansFromEvents(events: PipelineEvent[], detail: SessionDetail): Span[] | null {
  if (!events.length) return null;
  const withT = events.filter((e) => typeof e.t_ns === "number");
  if (!withT.length) return null;
  const t0 = Math.min(...withT.map((e) => Number(e.t_ns)));
  const spans: Span[] = [];
  let openLlm: {
    startMs: number;
    speculative?: boolean;
    prompt?: string;
    provider?: string;
    model?: string;
  } | null = null;
  let openTts: { startMs: number; text?: string } | null = null;
  let partialStt: number | null = null;
  const pendingSpecs: Span[] = [];
  let i = 0;

  const discardPendingAsSuperseded = () => {
    while (pendingSpecs.length) {
      const p = pendingSpecs.shift()!;
      if (p.kind === "spec") {
        p.kind = "spec-discarded";
        p.label = "Spec discarded";
      }
    }
  };

  const acceptPendingSpecs = () => {
    while (pendingSpecs.length) {
      const p = pendingSpecs.shift()!;
      if (p.kind === "spec") {
        p.kind = "spec-accepted";
        p.label = "Spec accepted";
      }
    }
  };

  for (const e of withT) {
    const name = String(e.event || "");
    const t = msFromNs(Number(e.t_ns), t0);
    const meta = e.meta || {};
    if (pushQualityFromEvent(spans, name, t, meta, `q-${i}`)) i += 1;

    if (name === "stt.first_partial") {
      if (partialStt == null) partialStt = t;
    } else if (name === "aux.guardrail" && Boolean(meta.yes)) {
      const lat = Math.max(80, Number(meta.latency_ms) || 120);
      const start = Math.max(0, t - lat);
      spans.push({
        id: `aux-guard-${i++}`,
        lane: "aux",
        startMs: start,
        endMs: Math.max(start + 80, t),
        kind: "guard",
        label: "Guard",
        text: String(meta.text || ""),
        model: meta.model != null ? String(meta.model) : undefined,
      });
    } else if (name === "tool.guardrail_redirect") {
      spans.push({
        id: `aux-redir-${i++}`,
        lane: "aux",
        startMs: Math.max(0, t - 40),
        endMs: t + 80,
        kind: "guard",
        label: meta.style === "adaptive" ? "Redirect (adaptive)" : "Redirect (fixed)",
        text: String(meta.text || ""),
      });
    } else if (name === "stt.final") {
      const start = partialStt != null ? partialStt : Math.max(0, t - 800);
      spans.push({
        id: `stt-${i++}`,
        lane: "stt",
        startMs: start,
        endMs: Math.max(start + 80, t),
        label: "Final",
        text: String(meta.text || ""),
      });
      partialStt = null;
    } else if (name === "llm.request_start") {
      const speculative = Boolean(meta.speculative);
      if (speculative && pendingSpecs.length) discardPendingAsSuperseded();
      openLlm = {
        startMs: t,
        speculative,
        prompt: meta.prompt != null ? String(meta.prompt) : undefined,
        provider: meta.provider != null ? String(meta.provider) : undefined,
        model: meta.model != null ? String(meta.model) : undefined,
      };
    } else if (name === "llm.output_done" && openLlm) {
      const speculative = Boolean(meta.speculative ?? openLlm.speculative);
      if (speculative) {
        const span: Span = {
          id: `llm-spec-${i++}`,
          lane: "llm",
          startMs: openLlm.startMs,
          endMs: Math.max(openLlm.startMs + 80, t),
          kind: "spec",
          label: "Spec",
          text: String(meta.text || ""),
          prompt: openLlm.prompt,
          provider: meta.provider != null ? String(meta.provider) : openLlm.provider,
          model: meta.model != null ? String(meta.model) : openLlm.model,
        };
        spans.push(span);
        pendingSpecs.push(span);
      } else {
        const isRegen = pendingSpecs.length > 0;
        const span: Span = {
          id: `llm-regen-${i++}`,
          lane: "llm",
          startMs: openLlm.startMs,
          endMs: Math.max(openLlm.startMs + 80, t),
          kind: isRegen ? "regen" : "accepted",
          label: isRegen ? "Regen" : "Reply",
          text: String(meta.text || ""),
          prompt: openLlm.prompt,
          provider: meta.provider != null ? String(meta.provider) : openLlm.provider,
          model: meta.model != null ? String(meta.model) : openLlm.model,
        };
        const from = pendingSpecs.pop();
        if (from) {
          from.kind = "spec-discarded";
          from.label = "Spec discarded";
          from.linkedToId = span.id;
          span.linkedFromId = from.id;
        }
        if (isRegen) {
          const drivingStt = [...spans]
            .reverse()
            .find((s) => s.lane === "stt" && s.endMs <= span.startMs + 50);
          if (drivingStt) {
            span.drivenBySttId = drivingStt.id;
            drivingStt.drivesRegenId = span.id;
          }
        }
        discardPendingAsSuperseded();
        spans.push(span);
      }
      openLlm = null;
    } else if (name === "llm.discarded" && openLlm) {
      spans.push({
        id: `llm-disc-${i++}`,
        lane: "llm",
        startMs: openLlm.startMs,
        endMs: Math.max(openLlm.startMs + 80, t),
        kind: openLlm.speculative ? "spec-discarded" : "discarded",
        label: openLlm.speculative ? "Spec discarded" : "Discarded",
        text: String(meta.error || meta.text || "Discarded draft"),
        prompt: openLlm.prompt,
        provider: openLlm.provider,
        model: openLlm.model,
      });
      openLlm = null;
    } else if (name === "tts.request_start" || name === "tts.provider_started") {
      if (!openTts) openTts = { startMs: t };
      if (name === "tts.request_start") {
        const match = Boolean(meta.match);
        const regen = Boolean(meta.regen);
        if (regen) discardPendingAsSuperseded();
        else if (match || pendingSpecs.length) acceptPendingSpecs();
        if (meta.text != null) openTts.text = String(meta.text);
      }
    } else if (name === "tts.first_text_chunk_sent" && openTts) {
      openTts.text = String(meta.text || "");
    } else if (name === "idle.bot_audio_end" && openTts) {
      spans.push({
        id: `tts-${i++}`,
        lane: "tts",
        startMs: openTts.startMs,
        endMs: Math.max(openTts.startMs + 80, t),
        label: "TTS",
        text: openTts.text,
      });
      openTts = null;
    }
  }

  acceptPendingSpecs();

  if (openTts) {
    spans.push({
      id: `tts-${i++}`,
      lane: "tts",
      startMs: openTts.startMs,
      endMs: openTts.startMs + 1200,
      label: "TTS",
      text: openTts.text,
    });
  }

  const assistantTurns = (detail.turns || []).filter((t) => t.role === "assistant" && t.audioUrl);
  for (const turn of assistantTurns) {
    const start = Number(turn.startMs || 0);
    const match = spans
      .filter((s) => s.lane === "tts")
      .sort((a, b) => Math.abs(a.startMs - start) - Math.abs(b.startMs - start))[0];
    if (match && Math.abs(match.startMs - start) < 4000) {
      match.audioUrl = turn.audioUrl;
      if (!match.text && turn.text) match.text = turn.text;
    }
  }
  for (const turn of detail.turns || []) {
    if (turn.role !== "user" || !turn.audioUrl) continue;
    const start = Number(turn.startMs || 0);
    const match = spans
      .filter((s) => s.lane === "stt")
      .sort((a, b) => Math.abs(a.startMs - start) - Math.abs(b.startMs - start))[0];
    if (match && Math.abs(match.startMs - start) < 4000) {
      match.audioUrl = turn.audioUrl;
    }
  }

  appendVideoQuality(spans, detail);
  return spans.length ? spans : null;
}

export function buildSpansFromTurns(detail: SessionDetail): Span[] {
  const spans: Span[] = [];
  const turns = detail.turns || [];
  const durationMs = Math.max(
    1000,
    Number(detail.durationSec || 0) * 1000,
    ...turns.map((t) => Number(t.endMs || t.startMs || 0) + 500),
  );

  turns.forEach((t, i) => {
    const start = Number(t.startMs ?? i * 2000);
    const end = Number(t.endMs ?? start + Math.max(600, Number(t.durationMs || 1200)));
    const role = String(t.role || "");
    if (role === "user") {
      spans.push({
        id: `stt-${i}`,
        lane: "stt",
        startMs: start,
        endMs: end,
        label: "Final",
        text: t.text || "",
        audioUrl: t.audioUrl,
      });
      const turnAny = t as {
        guardrail?: { yes?: boolean };
        guardrail_redirect?: unknown;
        guardrailRedirect?: unknown;
      };
      const fired = Boolean(
        turnAny.guardrail?.yes
        || turnAny.guardrail_redirect
        || turnAny.guardrailRedirect,
      );
      if (fired) {
        spans.push({
          id: `aux-guard-${i}`,
          lane: "aux",
          startMs: start,
          endMs: Math.max(start + 120, Math.min(end, start + 400)),
          kind: "guard",
          label: "Guard",
          text: t.text || "",
        });
      }
    } else if (role === "assistant") {
      spans.push({
        id: `tts-${i}`,
        lane: "tts",
        startMs: start,
        endMs: end,
        label: "TTS",
        text: t.text || "",
        audioUrl: t.audioUrl,
      });
      spans.push({
        id: `llm-acc-${i}`,
        lane: "llm",
        startMs: Math.max(0, start - 500),
        endMs: start,
        kind: "accepted",
        label: "Reply",
        text: t.text || "",
      });
    }
  });

  if (detail.providerDegraded) {
    const at = turns.find((t) => t.role === "assistant")?.startMs ?? durationMs * 0.35;
    spans.push({
      id: "q-degrade",
      lane: "quality",
      startMs: Number(at),
      endMs: Number(at),
      kind: "degrade",
      label: "Degrade",
      text: detail.degradedReason || detail.lastProviderError || "Provider failover",
    });
  }

  return spans;
}

export function buildLpSpans(rows: Array<Record<string, unknown>>): Span[] {
  const spans: Span[] = [];
  let i = 0;
  for (const row of rows) {
    if (String(row.event || "") !== "live_perception.tick") continue;
    const fuse = (row.fuse && typeof row.fuse === "object" ? row.fuse : {}) as Record<string, unknown>;
    const startMs = Math.max(0, Number(row.t_ms) || 0);
    const lang = row.language && typeof row.language === "object" ? (row.language as Record<string, unknown>) : {};
    const voice = row.voice && typeof row.voice === "object" ? (row.voice as Record<string, unknown>) : {};
    const video = row.video && typeof row.video === "object" ? (row.video as Record<string, unknown>) : {};
    const text = [
      `state ${String(fuse.state || "neutral")}`,
      fuse.winnerSrc ? `winner ${String(fuse.winnerSrc)}` : "",
      fuse.orukLabel ? `oruk ${String(fuse.orukLabel)}` : "",
      lang.label ? `language ${String(lang.label)}` : "",
      voice.status ? `voice ${String(voice.status)}` : "",
      video.summary ? `video ${String(video.summary)}` : "",
    ]
      .filter(Boolean)
      .join(" · ");
    spans.push({
      id: `lp-${i++}`,
      lane: "perception",
      startMs,
      endMs: startMs + 180,
      label: String(fuse.state || "tick"),
      text,
      frameUrl: row.frameUrl ? String(row.frameUrl) : undefined,
      display: "bar",
    });
  }
  return spans;
}

export function kindTitle(span: Span) {
  if (span.kind === "spec") return "Speculative draft";
  if (span.kind === "spec-accepted") return "Spec accepted";
  if (span.kind === "spec-discarded") return "Spec discarded";
  if (span.kind === "regen") return "Regenerated reply";
  if (span.kind === "accepted") return "Accepted reply";
  if (span.kind === "discarded") return "Discarded";
  if (span.kind === "degrade") return span.label || "Provider degrade";
  if (span.kind === "recovery") return "Recovery";
  if (span.kind === "in") return "In (Client request)";
  if (span.kind === "outcome") return "Outcome (brain)";
  if (span.kind === "income") return "Income (mouth)";
  if (span.kind === "tool") return "Tool";
  if (span.kind === "user") return "User";
  if (span.kind === "assistant") return "Mouth";
  return span.label || span.lane.toUpperCase();
}

export function barClassName(span: Span) {
  const parts = ["pipeline-bar"];
  if (span.display === "chip") parts.push("pipeline-chip");
  if (span.kind === "spec") parts.push("spec");
  if (span.kind === "spec-accepted") parts.push("spec", "spec-accepted");
  if (span.kind === "spec-discarded") parts.push("spec", "spec-discarded");
  if (span.kind === "regen") parts.push("regen");
  if (span.kind === "discarded") parts.push("discarded");
  if (span.kind === "user") parts.push("chip-user");
  if (span.kind === "assistant") parts.push("chip-mouth");
  if (span.kind === "in") parts.push("chip-in");
  if (span.kind === "outcome") parts.push("chip-outcome");
  if (span.kind === "income") parts.push("chip-income");
  if (span.kind === "tool") parts.push("chip-tool");
  return parts.join(" ");
}

export function draftLabel(span: Span) {
  if (span.kind === "spec" || span.kind === "spec-accepted" || span.kind === "spec-discarded") {
    return "Draft";
  }
  if (span.kind === "regen") return "Regen text";
  if (span.kind === "in") return "In";
  if (span.kind === "outcome") return "Outcome";
  if (span.kind === "income") return span.channel === "thinking" ? "Income (thinking)" : "Income (commentary)";
  return "Text";
}

export function formatClock(ms: number) {
  const n = Math.max(0, Math.round(ms));
  const sec = Math.floor(n / 1000);
  const rem = n % 1000;
  return `${sec}.${String(rem).padStart(3, "0")}s`;
}

export function formatSpanTiming(startMs: number, endMs: number): { range: string; duration: string | null } {
  const start = Math.round(startMs);
  const end = Math.round(endMs);
  const dur = Math.max(0, end - start);
  if (dur === 0) return { range: formatClock(start), duration: null };
  return {
    range: `${formatClock(start)} – ${formatClock(end)}`,
    duration: `${dur}ms`,
  };
}

export function laneHeading(lane: Span["lane"], mode: boolean | TimelineMode) {
  const direct = mode === true || mode === "direct";
  const liveavatar = mode === "liveavatar";
  if (lane === "aux") return liveavatar ? "session" : "guard";
  if (lane === "perception") return "live perc";
  if (lane === "transcript") return "transcript";
  if (lane === "delegation") return "handoff";
  if (direct && lane === "quality") return "quality";
  return lane;
}

export function chipColor(span: Span, mode?: TimelineMode): string {
  if (span.kind === "assistant") return mode === "liveavatar" ? "#ea580c" : "#0aa878";
  if (span.kind === "user") return "#0968f2";
  if (span.kind === "in") return "#5b8def";
  if (span.kind === "outcome") return "#8057e8";
  if (span.kind === "income") return "#c084fc";
  if (span.kind === "tool") return "#ea580c";
  return LANE_COLOR[span.lane];
}
